import React, { useState, useEffect } from 'react';
import useRepo from '../../hooks/useRepo';
import useApiContext from 'hooks/useApiContext';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { T, M, A } from 'TOMA';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputText, InputSelect } from '../../form';
import s from '../Event/Event.module.scss';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import routes from 'router/routes';

function UniverseCategoryFormPage(): JSX.Element {
  const { categoryRepository } = useApiContext();
  const { categoryId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [loadingForm, setLoadingForm] = useState(false);
  const [successForm, setSuccessForm] = useState(false);
  const [errorForm, setErrorForm] = useState<string>();
  const [categories, setCategories] = useState<any[]>([]);
  const { t } = useTranslation();
  const { response, loading } = useRepo(async () => {
    if (categoryId && categoryId !== 'new-universe') {
      return await categoryRepository.getCategoryById(categoryId);
    }
    return null;
  }, [categoryId, pathname]);

  const schema = yup.object({
    topicCode: yup.string().max(50).required(t('common:form.error.required')),
    code: yup.string().max(50).required(t('common:form.error.required')),
  });
  const form = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: {
      topic: '',
      topicCode: '',
      code: '',
      label: '',
      filter: '',
      label_en: '',
    },
  });

  const { handleSubmit, control, reset, getValues } = form;

  useEffect(() => {
    if (categoryId && !loading && response) {
      reset({
        topic: response.topic,
        topicCode: response.topicCode,
        code: response.code,
        label: response.label,
        filter: response.filter,
        label_en: response.label_en,
      });
    }
  }, [loading, response, reset, categoryId, categories]);

  function onSubmit(formData: any): void {
    setLoadingForm(true);
    setErrorForm('');
    setSuccessForm(false);

    // Transform the form data for API submission
    const apiData = {
      topic: 'universe',
      topicCode: formData.topicCode,
      code: formData.code,
      label: formData.label,
      filter: formData.filter,
      label_en: formData.label_en,
    };

    if (categoryId && categoryId !== 'new-universe') {
      categoryRepository
        .updateCategory(categoryId, apiData)
        .then(() => {
          toast.success(t('categories:update_success'));
          setSuccessForm(true);
          setTimeout(() => {
            navigate(routes.universes.path);
          }, 1500);
        })
        .catch((err: Error) => {
          setErrorForm(err.message);
          toast.error(err.message || t('categories:update_error'));
        })
        .finally(() => {
          setLoadingForm(false);
        });
    } else {
      categoryRepository
        .createCategory(apiData)
        .then(() => {
          toast.success(t('categories:create_success'));
          setSuccessForm(true);
          setTimeout(() => {
            navigate(routes.universes.path);
          }, 1500);
        })
        .catch((err: Error) => {
          console.log('Error creating category:', err);
          setErrorForm(err.message);
          toast.error(err.message || t('categories:create_error'));
        })
        .finally(() => {
          setLoadingForm(false);
        });
    }
  }

  return (
    <M.Loading loading={loading} error={null} fullWiewport>
      <T.Container py="50">
        <T.Block pb="50" mw="medium" m="auto" p="20" borderRadius="7">
          <A.Title as="h1" fontSize="48" mb="30" fw="black" textAlign="center">
            {categoryId && categoryId !== 'new-universe'
              ? t('categories:edit_universe_category')
              : t('categories:create_a_new_universe_category')}
          </A.Title>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* <T.Block mb="20">
              <InputText
                label={t('categories:topic')}
                name="topic"
                type="text"
                control={control}
                disabled={false}
                required
              />
            </T.Block> */}

            <T.Block mb="20">
              <InputText
                label={t('categories:topicCode')}
                name="topicCode"
                type="text"
                control={control}
                disabled={false}
                required
              />
            </T.Block>
            <T.Block mb="20">
              <InputText
                label={t('categories:code')}
                name="code"
                type="text"
                control={control}
                disabled={false}
                required
              />
            </T.Block>
            <T.Block mb="20">
              <InputText
                label={t('categories:label')}
                name="label"
                type="text"
                control={control}
                disabled={false}
                required
              />
            </T.Block>
            <T.Block mb="20">
              <InputText
                label={t('categories:filter')}
                name="filter"
                type="text"
                control={control}
                disabled={false}
                required
              />
            </T.Block>
            <T.Block mb="20">
              <InputText
                label={t('categories:label_english')}
                name="label_en"
                type="text"
                control={control}
                disabled={false}
                required
              />
            </T.Block>
            <T.Block mt="30" mb="20">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                <A.Button
                  type="submit"
                  bg="success"
                  loading={loadingForm}
                  disabled={loadingForm}
                >
                  {categoryId && categoryId !== 'new-universe'
                    ? t('common:update')
                    : t('common:create')}
                </A.Button>
                {/* <A.Button
                  type="button"
                  appearance="link"
                  color="grey-80"
                  textDecoration="underline"
                  onClick={() => navigate(routes.categories.path)}
                >
                  {t('common:cancel')}
                </A.Button> */}
              </div>
            </T.Block>

            <T.Block mt="20">
              <A.Alert alert="success" side="🎉" mb="15">
                {successForm &&
                  (categoryId && categoryId !== 'new-universe'
                    ? t('categories:update_success')
                    : t('categories:create_success'))}
              </A.Alert>
              <A.Alert alert="error" side="⚠️" mb="15">
                {errorForm && <A.InnerHTML html={errorForm} />}
              </A.Alert>
            </T.Block>
          </form>
        </T.Block>
      </T.Container>
    </M.Loading>
  );
}

export default UniverseCategoryFormPage;
